import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TokenService } from '../../../core/services/core.token.service';
import { SseEvent, streamChat as sseStreamChat } from './chat.sse-parser';
import { STUB_MODE, STUB_TOPICS, STUB_MESSAGES } from './chat.flow.stubs';

export interface FlowTopic {
    id: string;
    title: string;
    user_id: number;
    project_id: number;
    is_shared: boolean;
    is_pinned: boolean;
    meta: Record<string, unknown>;
    create_date: string;
}

export interface FlowCitation {
    source_id: number;
    title: string | null;
    section: string;
    ref_id: number;
    source_url: string | null;
    project_name: string;
}

export interface FlowMessage {
    id: number;
    topic_id: string;
    user_id: number;
    role: 'user' | 'assistant' | 'system';
    content: string;
    meta: Record<string, unknown>;
    create_date: string;
    streaming?: boolean;
    error?: boolean;     // in-memory only — marks a failed assistant message
    citations?: FlowCitation[];  // in-memory only — populated from done SSE event
}

export interface LlmInfo {
    provider: string;
    model: string;
    configured: boolean;
}

@Injectable()
export class ChatFlowService {

    constructor(
        private http: HttpClient,
        private tokenService: TokenService
    ) {}

    private topicBase(projectId: string | number): string {
        return `/flow/project/${projectId}/topic`;
    }

    private chatBase(projectId: string | number): string {
        return `/flow/project/${projectId}/chat`;
    }

    listTopics(projectId: string | number): Promise<FlowTopic[]> {
        if (STUB_MODE) { return Promise.resolve(STUB_TOPICS); }
        return this.http.get<FlowTopic[]>(this.topicBase(projectId))
            .toPromise();
    }

    listMessages(projectId: string | number, topicId: string): Promise<FlowMessage[]> {
        if (STUB_MODE) { return Promise.resolve(STUB_MESSAGES); }
        return this.http.get<FlowMessage[]>(
            `${this.topicBase(projectId)}/${topicId}/messages?order=asc`
        ).toPromise();
    }

    streamChat(projectId: string | number, topicId: string, content: string): Observable<SseEvent> {
        return sseStreamChat(
            `${this.chatBase(projectId)}/${topicId}`,
            { content },
            { 'GFAuthToken': this.tokenService.getToken() || '' }
        );
    }

    createTopic(projectId: string | number, firstMessage?: string): Promise<FlowTopic> {
        const body: { first_message?: string } = {};
        if (firstMessage) {
            body.first_message = firstMessage;
        }
        return this.http.post<FlowTopic>(this.topicBase(projectId), body)
            .toPromise();
    }

    updateTopic(projectId: string | number, topicId: string, data: { title?: string; is_shared?: boolean; is_pinned?: boolean }): Promise<FlowTopic> {
        return this.http.post<FlowTopic>(`${this.topicBase(projectId)}/${topicId}`, data)
            .toPromise();
    }

    deleteTopic(projectId: string | number, topicId: string): Promise<void> {
        return this.http.delete<void>(`${this.topicBase(projectId)}/${topicId}`)
            .toPromise();
    }

    getLlmInfo(projectId: string | number): Promise<LlmInfo> {
        return this.http.get<LlmInfo>(`/flow/project/${projectId}/llm-info`)
            .toPromise()
            .catch(() => ({ configured: false, provider: '', model: '' }));
    }

    getAiSettings(projectId: number): Promise<any> {
        return this.http.get<any>(`/api/setting/${projectId}/aiconfig`)
            .toPromise()
            .catch(() => null);
    }
}
